import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, styled } from '@mui/material';
import { Modal } from '../Modal';
import { Text } from '../../atoms/typography';
import SelectInput from '../../atoms/SelectInput';
import Scanner from '../Scanner';
import { useSnackbar } from '../../../wrappers';
import { Alert } from '../../atoms/Alert';
import Checkbox from '../../atoms/Checkbox';
const TextWithTopMargin = styled(Text) `
  margin-top: 24px;
`;
const SelectWithTopMargin = styled(SelectInput) `
  margin-top: 24px;
`;
;
const FacilityModal = ({ onSave, onOpen, facilities, facility: initialFacility, workstation: initialWorkstation, isOpen, onClose, isLoading = false, browserPrinterOptions = [], printerServiceConnected, useLocationScan = false, }) => {
    const environment = process.env.YERDLE_ENV;
    const showSnackbar = useSnackbar();
    const [facility, setFacility] = useState(initialFacility);
    const [workstation, setWorkstation] = useState(initialWorkstation);
    const [facilityError, setFacilityError] = useState(false);
    const [printerError, setPrinterError] = useState(false);
    const [workstationError, setWorkstationError] = useState(false);
    const [facilityOptions, setFacilityOptions] = useState([]);
    const [workstationOptions, setWorkstationOptions] = useState([]);
    const [selectedPrinters, setSelectedPrinters] = useState({});
    const [locationScanError, setLocationScanError] = useState();
    const [noPrintersAcknowledged, setNoPrintersAcknowledged] = useState(false);
    const getInitialPrinters = (warehouseId, workstationId) => {
        const warehouseWorkstations = facilities?.find(({ id }) => id === Number(warehouseId))?.workstations ?? [];
        const workstation = warehouseWorkstations?.find(({ id }) => id === Number(workstationId));
        return {
            browserLabel4x2Printer: workstation?.browserLabel4x2Printer || '',
            browserSlipPrinter: workstation?.browserSlipPrinter || '',
            browserPostagePrinter: workstation?.browserPostagePrinter || '',
        };
    };
    const { enableBrowserBasedPrinting, requiredPrintersPreselected, noPrintersRequired } = useMemo(() => {
        const currentFacility = facilities?.find(({ id }) => id === Number(facility ?? '-1'));
        const currentWorkstation = currentFacility?.workstations?.find(({ id }) => id === Number(workstation ?? '-1'));
        const enableBrowserBasedPrinting = !!currentFacility?.useBrowserBasedPrinting;
        const noPrintersRequired = currentWorkstation?.isPhysicalPrintout === false;
        const initialPrinters = getInitialPrinters(facility, workstation);
        setSelectedPrinters(initialPrinters);
        const requiredPrintersPreselected = !enableBrowserBasedPrinting || !noPrintersRequired || (!workstation ? undefined : (!!initialPrinters.browserLabel4x2Printer ||
            !!initialPrinters.browserSlipPrinter ||
            !!initialPrinters.browserPostagePrinter));
        return {
            enableBrowserBasedPrinting,
            requiredPrintersPreselected,
            noPrintersRequired,
        };
    }, [facility, workstation, JSON.stringify(facilities.map(f => f.id).sort())]);
    const requiredPrintersSelected = useMemo(() => {
        console.log({
            enableBrowserBasedPrinting,
            noPrintersRequired,
            workstation,
            selectedPrinters,
            hasSelectedPrinters: Object.values(selectedPrinters).some(v => !!v),
        });
        return !enableBrowserBasedPrinting || noPrintersRequired || ((Object.values(selectedPrinters).some(v => !!v)));
    }, [enableBrowserBasedPrinting, noPrintersRequired, workstation, JSON.stringify(selectedPrinters)]);
    const wasMissingPrinters = useRef(requiredPrintersPreselected === false);
    useEffect(() => {
        if (isOpen) {
            onOpen?.();
        }
    }, [isOpen]);
    // This is important. It informs the auto save logic when a work station does not already have
    // printers selected and prevents the auto save after the last printer is selected.
    useEffect(() => {
        wasMissingPrinters.current = wasMissingPrinters.current || requiredPrintersPreselected === false;
    }, [requiredPrintersPreselected]);
    const isEditing = useMemo(() => {
        return !!(initialFacility && initialWorkstation);
    }, []);
    /**
     * This useEffect formats the facilities and workstations into options for the select inputs.
     * It also handles the case where there is only one facility or workstation available,
     * automatically selecting it, or clearing the selection if the current facility or workstation
     * is no longer available.
     */
    useEffect(() => {
        const facilityOptions = facilities.map(facility => ({
            label: facility.code ?? 'Missing Facility Name',
            value: String(facility.id),
            id: facility.id,
            useBrowserBasedPrinting: facility.useBrowserBasedPrinting,
        }));
        setFacilityOptions(facilityOptions);
        const currentFacilityId = facility ?? initialFacility;
        const currentFacility = facilities.find(facility => facility.id === Number(currentFacilityId));
        if (facilityOptions.length === 1) {
            const option = facilityOptions[0];
            // Auto select only option when there's only one available.
            setFacility(option.value);
        }
        else if (currentFacilityId && !currentFacility) {
            // Clear the facility as its not an option now.
            setFacility(undefined);
        }
        const workstationOptions = currentFacility?.workstations.map(workstation => ({
            label: workstation.displayName,
            value: String(workstation.id),
            id: workstation.id,
            browserLabel4x2Printer: workstation.browserLabel4x2Printer,
            browserSlipPrinter: workstation.browserSlipPrinter,
            browserPostagePrinter: workstation.browserPostagePrinter,
        })) || [];
        setWorkstationOptions(workstationOptions);
        const currentWorkstationId = workstation ?? initialWorkstation;
        const currentWorkstation = workstationOptions.find(ws => ws.value === currentWorkstationId);
        if (workstationOptions.length === 1) {
            // Auto select only option when there's only one available.
            setWorkstation(workstationOptions[0].value);
        }
        else if (currentWorkstationId && !currentWorkstation) {
            // clear the workstation as its not an option now.
            setWorkstation(undefined);
        }
    }, [JSON.stringify(facilities.map(({ id }) => id).sort()), facility, initialFacility, workstation, initialWorkstation]);
    const handlePrinterChange = (printerType, newPrinter) => {
        setPrinterError(false);
        if (newPrinter && selectedPrinters) {
            setSelectedPrinters(prev => ({
                ...prev,
                [printerType]: newPrinter,
            }));
        }
    };
    const handleWorkstationChange = (newWorkstation) => {
        setWorkstationError(false);
        setNoPrintersAcknowledged(false);
        setWorkstation(newWorkstation);
        setPrinterError(false);
    };
    const handleFacilityChange = (newFacility) => {
        setFacilityError(false);
        setFacility(newFacility);
        handleWorkstationChange(undefined);
    };
    function validateUuid(uuid) {
        return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(uuid);
    }
    const handleLocationScannerChange = (scannedValue) => {
        setLocationScanError(undefined);
        const trimmedValue = scannedValue?.trim() || '';
        const isPrefixed = trimmedValue.startsWith('loc-') || trimmedValue.startsWith('shl-');
        const formattedValue = isPrefixed ? trimmedValue.slice(4) : trimmedValue;
        const [code, uuid] = !isPrefixed ?
            [formattedValue, formattedValue] :
            (trimmedValue.startsWith('loc-') ?
                [undefined, formattedValue] :
                [formattedValue, undefined]);
        const { facility: foundFacility, workstation: foundWorkstation, } = facilities.reduce((acc, facility) => {
            if (acc.facility && acc.workstation)
                return acc;
            const workstation = facility.workstations.find((ws) => {
                if (ws.location) {
                    if (environment !== 'production') {
                        return ws.location?.code === code || ws.location?.uuid === uuid;
                    }
                    else {
                        if (uuid) {
                            const isUuid = validateUuid(uuid);
                            if (!isUuid) {
                                return false;
                            }
                            else {
                                return ws.location?.uuid === uuid;
                            }
                        }
                    }
                }
                return false;
            });
            return workstation ? { workstation, facility } : acc;
        }, { facility: undefined, workstation: undefined });
        if (foundFacility && foundWorkstation) {
            setFacility(foundFacility.id.toString());
            setWorkstation(foundWorkstation.id.toString());
        }
        else {
            if (trimmedValue.length) {
                setLocationScanError(`Invalid workstation scanned. ${environment === 'production' ? 'Ensure you insert a valid UUID.' : ''}`);
            }
            setFacility(undefined);
            setWorkstation(undefined);
        }
    };
    const selectionPrompt = useMemo(() => {
        return workstation ?
            `Current workstation: ${workstationOptions.find(ws => ws.value === workstation)?.label || 'Invalid Workstation'}` :
            'Scan your workstation.';
    }, [workstation, JSON.stringify(workstationOptions)]);
    const showPrinterSelectionOptions = useMemo(() => (enableBrowserBasedPrinting && !!facility && !!workstation), [enableBrowserBasedPrinting, !!facility, !!workstation]);
    const handleClose = (shouldReset) => {
        setFacility(initialFacility);
        setWorkstation(initialWorkstation);
        setFacilityError(false);
        setWorkstationError(false);
        setLocationScanError(undefined);
        setSelectedPrinters(getInitialPrinters(initialFacility, initialWorkstation));
        onClose?.(shouldReset);
    };
    const handleSave = () => {
        const isMissingPrinters = !requiredPrintersSelected && !noPrintersAcknowledged;
        setFacilityError(!facility);
        setWorkstationError(!workstation);
        setPrinterError(isMissingPrinters);
        const hasErrors = !facility || !workstation || isMissingPrinters;
        if (hasErrors) {
            console.log('hasErrors', {
                facility,
                workstation,
                isMissingPrinters,
                noPrintersAcknowledged,
            });
            return;
        }
        onSave(facility, workstation, 
        // Only pass selected printers if browser-based printing is enabled.
        enableBrowserBasedPrinting ? selectedPrinters : undefined, 
        // Handles the edge case of checking the box and then selecting a printer.
        noPrintersAcknowledged && !requiredPrintersSelected);
        const selectedWorkstation = facilities.find(({ id }) => id === Number(facility))?.
            workstations?.find(({ id }) => id === Number(workstation))?.displayName ||
            'Invalid Workstation';
        showSnackbar({
            message: `Now using workstation: ${selectedWorkstation}`,
            severity: 'success',
            duration: 3000,
        });
        handleClose(false);
    };
    const hasPrinterIssue = useMemo(() => {
        return enableBrowserBasedPrinting && !!facility && !!workstation && !noPrintersRequired && (!requiredPrintersPreselected ||
            browserPrinterOptions.length === 0 ||
            printerServiceConnected === false);
    }, [
        facility,
        workstation,
        printerServiceConnected,
        browserPrinterOptions.length,
        requiredPrintersPreselected,
        noPrintersRequired,
    ]);
    /**
     * This useEffect checks if the modal is selecting the workstation for the first time and when
     * browser-based printing is enabled, if all printers are selected. If this is all true, it will
     * automatically save the selections and close the modal. If it is being edited, it will not
     * automatically save.
     */
    useEffect(() => {
        const canAutoSave = !noPrintersRequired &&
            !hasPrinterIssue &&
            useLocationScan &&
            !isEditing &&
            facility &&
            workstation &&
            !wasMissingPrinters.current;
        if (canAutoSave) {
            handleSave();
        }
    }, [
        facility,
        workstation,
        JSON.stringify(selectedPrinters),
        hasPrinterIssue,
        noPrintersRequired,
    ]);
    const noPrintersAcknowledgment = enableBrowserBasedPrinting ? (_jsx(Checkbox, { name: 'no-printers-acknowledgment', label: "I acknowledge printing will not be available for this session.", checked: noPrintersAcknowledged, onChange: (e) => setNoPrintersAcknowledged(e?.target?.checked) }, `no-printers-acknowledgment-${noPrintersAcknowledged}`)) : undefined;
    const content = (_jsxs(Box, { children: [!useLocationScan ? (_jsxs(_Fragment, { children: [_jsx(Text, { children: "Set your current facility and workstation. These determine how inventory items are tracked and which printer will be used." }), _jsx(TextWithTopMargin, { children: "You can change your settings later in the sidebar." }), _jsx(SelectWithTopMargin, { label: 'Facility', fullWidth: true, options: facilityOptions, onChange: (event) => handleFacilityChange(event?.target?.value), isRequired: true, hasError: facilityError, initialValue: facility }), _jsx(SelectWithTopMargin, { label: 'Workstation', fullWidth: true, options: workstationOptions, onChange: (event) => handleWorkstationChange(event?.target?.value), isRequired: true, hasError: workstationError, initialValue: workstation, isDisabled: !facility })] })) : (_jsxs(_Fragment, { children: [_jsx(Text, { children: selectionPrompt }), _jsx("br", {}), _jsx(Scanner, { onScannerChange: async (scannedValue) => {
                            handleLocationScannerChange(scannedValue?.currentTarget?.value ?? '');
                        }, showIcon: true, label: 'QR Code', hasError: !!locationScanError, disabled: !!isLoading, helperText: locationScanError, onClear: locationScanError ? (() => handleLocationScannerChange('')) : undefined })] })), showPrinterSelectionOptions && (!printerServiceConnected && !noPrintersRequired ? (_jsxs(Alert, { title: 'Printing Service Not Available', severity: 'warning', sx: { mt: 1 }, children: [_jsx(Text, { children: "No printing service was found. Please ask your supervisor for support." }), noPrintersAcknowledgment] })) :
                !browserPrinterOptions?.length && !noPrintersRequired ? (_jsxs(Alert, { title: 'No Printers Available', severity: 'warning', sx: { mt: 1 }, children: [_jsx(Text, { children: "No printers were found on your network. Please ask your supervisor for support." }), noPrintersAcknowledgment] })) :
                    noPrintersRequired ? (_jsx(Alert, { title: 'No Printers Needed', severity: 'info', sx: { mt: 1 }, children: _jsx(Text, { children: "No printers are required for this workstation." }) })) : (_jsxs(_Fragment, { children: [_jsx(SelectWithTopMargin, { label: '4x2 Printer', fullWidth: true, options: browserPrinterOptions, value: selectedPrinters?.browserLabel4x2Printer || '', onChange: (event) => handlePrinterChange('browserLabel4x2Printer', event?.target?.value), disabled: !workstation, hasError: printerError }), _jsx(SelectWithTopMargin, { label: 'Slip Printer', fullWidth: true, value: selectedPrinters?.browserSlipPrinter || '', options: browserPrinterOptions, onChange: (event) => handlePrinterChange('browserSlipPrinter', event?.target?.value), disabled: !workstation, hasError: printerError }), _jsx(SelectWithTopMargin, { label: 'Postage Printer', fullWidth: true, options: browserPrinterOptions, onChange: (event) => handlePrinterChange('browserPostagePrinter', event?.target?.value), value: selectedPrinters?.browserPostagePrinter || '', disabled: !workstation, hasError: printerError }), !requiredPrintersSelected && !noPrintersRequired && (_jsxs(Alert, { title: 'No Printers Selected', severity: 'warning', sx: { mt: 1 }, children: [_jsx(Text, { children: "No printers have been selected for this workstation. Please ask your supervisor for support." }), noPrintersAcknowledgment] }))] })))] }));
    return _jsx(Modal, { title: 'Facility and workstation', actions: [{
                label: 'Save',
                onClick: handleSave,
                color: 'primary',
                variant: 'contained',
                disabled: (!requiredPrintersSelected || hasPrinterIssue) && !noPrintersAcknowledged,
            }], hideCloseButton: !(initialFacility && initialWorkstation), content: content, isOpen: isOpen, onClose: () => handleClose(true), isLoading: isLoading, sx: {
            // Disable closing the modal by clicking outside of it.
            // It will avoid losing the selected data when clicking outside the modal.
            '& .MuiBackdrop-root': {
                pointerEvents: 'none',
            },
        } });
};
export default FacilityModal;
//# sourceMappingURL=index.js.map